import { batch, closest, reactive, toJS } from '@rehaje/reactive'
import {
  assign,
  different,
  getIn,
  getPrototypeOf,
  getProxyRaw,
  isArray,
  isEqual,
  isPrivateKey,
  isProxyRawKey,
  isTerminateValue,
  keys,
  localeCompareArray,
  objectPrototype,
  primitiveDiff,
  recursiveTraverse,
  setIn,
  traverse,
  traverseIn,
  traverseTo,
  uid
} from '@rehaje/shared'

import { RR } from '../alias'
import { DefaultIndexes, UnknownSchema } from '../constants'
import {
  FatalAssertOverrideError,
  FatalCrossStoreError,
  FatalMissingContextError,
  FatalSelfInsertError
} from '../errors'
import {
  AddNodeEvent,
  MoveNodeEvent,
  RemoveNodeEvent
} from '../events/TypedNode'
import { calcDepth } from '../lambdas/calcArrayDepth'
import { createArrayLike } from '../lambdas/createArrayLike'
import { createArrayProxy } from '../lambdas/createArrayProxy'
import { getMemoizedKeys } from '../lambdas/getMemoizedKeys'
import { is } from '../lambdas/is'
import { memoize } from '../lambdas/memoize'
import {
  getElementInNArray,
  setNArray,
  traverseElementsInNArray
} from '../lambdas/nArray'
import { parseID } from '../lambdas/parseID'
import { suspend } from '../lambdas/suspend'
import {
  toJSByIterableKeys,
  toJSONByIterableKeys
} from '../lambdas/toJSByIterableKeys'
import {
  addElementEffect,
  addNodeEffect,
  addNodeEvaluate,
  addNodeMove,
  runElementEffect,
  runNodeEffect,
  runNodeEvaluate,
  runNodeMove
} from '../queues'
import { isSkipCreateTypedModel } from '../skip'
import { LifeCycle } from './LifeCycle'
import { TypedScope } from './TypedScope'
import { ViewElement } from './ViewElement'
import { ViewModel } from './ViewModel'

import type { Store } from '../stores/Store'
import type {
  AnalyzeInfo,
  Dependency,
  Different,
  EffectOptions,
  ForkOptions,
  InternalNode,
  MoveNodeItem,
  RehajePlugin,
  Result,
  ScopeDeclarations,
  SearchMatch,
  SearchOptions,
  SearchResultItem,
  SnapshotSpecificLike,
  TypedNodeConstructor,
  TypedSchema,
  TypedSpecific
} from '../types'
import type { TypedDefinition } from './TypedDefinition'
import type { ViewScope } from './ViewScope'
import type { ObserveEvent, ObserveOptions, Observer } from '@rehaje/reactive'
import type {
  Callback,
  Disposer,
  Filter,
  TIterator,
  toJSON,
  TRecord
} from '@rehaje/shared'

/**
 * ADT DSL 节点模型基类，为 DSL 动态操作提供了完备丰富的能力
 */
export abstract class TypedNode
  extends LifeCycle
  implements InternalNode<TypedNode>
{
  static __r_auto_counter = 0

  static __r_auto_graph_counter = 0

  static __r_create_node_counter = 0

  /**
   * 深度默认值合并策略：
   * - 默认不深合并
   */
  static __r_deep_defaults_merge = false

  /**
   * 默认值合并策略：
   * - 默认不合并默认值，只有通过 batchDefaultsMerge 函数包装的情况下才会进行默认值合并
   * - 不合并默认值场景（不包装 batchDefaultsMerge）
   *   - 首次渲染场景、复制粘贴场景、撤销重做场景
   * - 合并默认值场景（包装 batchDefaultsMerge）
   *   - 物料面板拖拽场景、修改属性面板场景、引导式搭建场景
   */
  static __r_defaults_merge = false

  /**
   * 控制自动建模的时候如何处理传入存在相同 id 的 json，
   * true: 创建 id 自增的全新节点，false: 移动已存在 id 的实例。
   *
   * 依赖点：
   * 1. rehaje 撤销重做（移动实例）
   * 2. builder 属性面板（移动实例）
   * 3. builder 引导式搭建（id 自增）
   * 4. 其他默认（id 自增）
   */
  static __r_increase_id = true

  static __r_injection_belongs?: PropertyKey[]

  static __r_injection_parent?: TypedNode

  static __r_injection_store?: Store

  static __r_moving_cross_tree = false

  static __r_origin = Symbol('__typed_node_origin__')

  static __r_spawning_node = false

  static __r_transacting_ids?: string[]

  __r__children: TypedNode[] = []

  @reactive('ref', { observe: false })
  __r__children_version: number = 0

  __r__counter?: Record<string, number>

  __r__ids?: Set<string>

  declare __r__unique_id: string

  @reactive('ref', { observe: false })
  __r_belongs: PropertyKey[] = []

  override __r_debug_id = `typedNode:${uid()}`

  __r_elements_graph: ViewElement[] = []

  __r_errors?: Record<string, Error>

  declare __r_from_keys: PropertyKey[]

  declare __r_from_maps: Record<PropertyKey, true>

  __r_get_view_elements = memoize((): (undefined | ViewElement)[] => {
    const elements: (undefined | ViewElement)[] = []
    this.__r_traverseElement((element) => elements.push(element))
    return elements
  })

  @reactive('ref', { observe: false })
  __r_id!: string

  declare __r_lazy_maps: Record<PropertyKey, any>

  @reactive('ref', { observe: false })
  __r_parent?: TypedNode

  declare __proto__: TypedNode

  declare __r_proxies: Map<TypedNode, TypedNode>

  declare __r_proxy_raw: this | undefined

  declare __r_scope_declaration: ScopeDeclarations

  __r_snapshot_id?: string

  __r_spawned = TypedNode.__r_spawning_node

  declare __r_start_evaluated: boolean

  __r_store: Store

  __r_type: string

  __r_typed_scope: TypedScope

  __r_version?: string

  declare getPlugin: <T extends RehajePlugin>() => T

  @reactive('computed')
  get __r_address(): PropertyKey[] {
    if (this.__r_parent) {
      return [...this.__r_parent.__r_address, ...this.__r_belongs]
    }
    return []
  }

  @reactive('computed')
  get __r_address_source() {
    return this.__r_address.join('/')
  }

  @reactive('computed')
  get __r_belongs_property() {
    if (this.__r_belongs.length === 0) return
    return getIn(this.__r_parent, this.__r_belongs.slice(0, -1))
  }

  @reactive('computed')
  get __r_belongs_source() {
    return this.__r_belongs.join('/')
  }

  get __r_children() {
    void this.__r__children_version
    return this.__r__children
  }

  /**
   * 节点计数表
   */
  get __r_counter(): TRecord<number> {
    if (this.__r_root !== this) return this.__r_root.__r_counter
    this.__r__counter ??= {}
    return this.__r__counter
  }

  @reactive('computed')
  get __r_descendants(): TypedNode[] {
    return this.__r_children.flatMap((node) => [node, ...node.__r_descendants])
  }

  @reactive('computed')
  get __r_entity_belongs(): PropertyKey[] {
    const parent = this.__r_parent
    if (parent) {
      if (parent.isEntityNode()) return this.__r_belongs
      return [...parent.__r_entity_belongs, ...this.__r_belongs]
    }
    return []
  }

  @reactive('computed')
  get __r_entity_parent(): TypedNode | undefined {
    const parent = this.__r_parent
    if (parent?.isEntityNode()) return parent
    return parent?.__r_entity_parent
  }

  @reactive('computed')
  get __r_exposed_schema(): TypedSchema {
    const defStore = this.__r_store.definition
    if (!defStore.hasTypedDefinition(this.type)) return UnknownSchema
    const model = this.getExposedViewModel()
    return defStore.__r_injectNode(() => defStore.infer(model), this)
  }

  get __r_ids(): Set<string> {
    if (this.__r_root !== this) return this.__r_root.__r_ids
    this.__r__ids ??= new Set()
    return this.__r__ids
  }

  get __r_indexes(): (number | string)[] {
    return DefaultIndexes as (number | string)[]
  }

  @reactive('computed')
  get __r_location() {
    if (this.isEntityNode()) return [this.id]
    const parent = this.__r_entity_parent
    if (parent) return [parent.id, ...this.__r_entity_belongs]
    return []
  }

  @reactive('computed')
  get __r_location_source() {
    return this.__r_location.join('.')
  }

  @reactive('computed')
  get __r_node_map(): Map<string, TypedNode> {
    if (this.__r_root !== this) return this.__r_root.__r_node_map
    const table: Map<string, TypedNode> = new Map()
    const nodes = this.__r_nodes
    for (const node of nodes) {
      if (node.id) table.set(node.id, node)
    }
    return table
  }

  @reactive('computed')
  get __r_nodes(): TypedNode[] {
    if (this.__r_root !== this) return this.__r_root.__r_nodes
    return [this, ...this.__r_descendants]
  }

  @reactive('computed')
  get __r_order(): number {
    const parent = this.__r_parent
    if (!parent) return 0
    const previous = this.__r_previous
    if (!previous) return parent.__r_order + 1
    return previous.__r_order + previous.__r_descendants.length + 1
  }

  @reactive('computed')
  get __r_parents(): TypedNode[] {
    if (this.__r_parent) {
      return [this.__r_parent, ...this.__r_parent.__r_parents]
    }
    return []
  }

  @reactive('computed')
  get __r_previous(): TypedNode | undefined {
    const parent = this.__r_parent
    if (!parent) return undefined
    const children = parent.__r_children.toSorted((a, b) => {
      return localeCompareArray(a.__r_belongs, b.__r_belongs)
    })
    const index = children.findIndex((child) => this.is(child))
    return children[index - 1]
  }

  @reactive('computed')
  get __r_root(): TypedNode {
    return this.__r_parent?.__r_root ?? this
  }

  @reactive('computed')
  get __r_schema(): TypedSchema {
    const defStore = this.__r_store.definition
    if (!defStore.hasTypedDefinition(this.type)) return UnknownSchema
    return defStore.__r_injectNode(() => defStore.infer(this), this)
  }

  get __r_typed_definition() {
    return this.__r_store.definition.find(this.type)
  }

  get __r_unique_id() {
    if (this.__r__unique_id) return this.__r__unique_id
    this.__r__unique_id = uid()
    return this.__r__unique_id
  }

  @reactive('computed')
  get __r_unique_node_map(): Map<string, TypedNode> {
    if (this.__r_root !== this) return this.__r_root.__r_unique_node_map
    const table: Map<string, TypedNode> = new Map()
    for (const node of this.__r_nodes) {
      table.set(node.__r_unique_id, node)
    }
    return table
  }

  /**
   * 节点 ID(可读可写)
   * 规则
   * 1. 初始化建模时
   *    1. 协议不传 id，自动变自增 id
   *    2. 协议传 id，检测冲突变自增 id
   * 2. 同树移动时 id 不变
   * 3. 跨树移动时，检测冲突变自增 id，不冲突保持不变
   * 4. 节点删除时，自增重置从 1 开始
   * 5. 手动修改时，禁止设置重复 id
   * 6. 自增规则以当前 id 的 {increaseKey}{increaseIndex} 中的 increaseKey 部分进行计数(想要定制拼接规则可以通过重载 getIncreaseID 实现)
   */
  @reactive('observe')
  get id(): string {
    return this.__r_id
  }

  // 手动修改 ID，冲突检测
  set id(id: string | undefined) {
    if (id && id !== this.__r_id) {
      try {
        this.__r_cleanupID()
        this.__r_setSelfID(id)
      } catch {}
    }
  }

  /**
   * ADT 类型签名(只读)
   */
  get type() {
    return this.__r_type
  }

  set type(type: string) {
    if (type === this.type) return
    this.replace({ type })
  }

  /**
   * 当前协议版本
   */
  get version() {
    return this.__r_version ?? '0.0.0'
  }
  constructor(specific: TypedSpecific)

  constructor(specific: SnapshotSpecificLike) {
    super()
    if (!TypedNode.__r_injection_store) {
      throw new FatalMissingContextError(
        specific.type,
        'Store',
        'createTypedNode'
      )
    }
    this.__r_type = specific.type
    this.__r_version = specific.version
    this.__r_store = TypedNode.__r_injection_store
    this.__r_parent = TypedNode.__r_injection_parent
    this.__r_belongs = TypedNode.__r_injection_belongs ?? []

    this.__r_typed_scope = new TypedScope(this)
    this.__r_setSnapshotID(specific.__r_snapshot_id)
    this.__r_setSelfID(specific.id)
    this.__r_startEvaluate()
    this.__r_fromSpecific(specific)
    this.__r_startAutomate()
  }

  /**
   * 包装函数内所有建模过程填充默认值
   */
  static __r_batchDefaultsMerge<T = any>(
    callback: Callback<T>,
    deepDefaultsMerge = false
  ): T {
    const prevDefaultsMerge = TypedNode.__r_defaults_merge
    const prevDeepDefaultsMerge = TypedNode.__r_deep_defaults_merge
    TypedNode.__r_defaults_merge = true
    TypedNode.__r_deep_defaults_merge = deepDefaultsMerge
    try {
      return callback()
    } finally {
      TypedNode.__r_defaults_merge = prevDefaultsMerge
      TypedNode.__r_deep_defaults_merge = prevDeepDefaultsMerge
    }
  }

  static __r_batchElementEffect(elements: ViewElement[]): void {
    for (const element of elements) element.effect()
  }

  static __r_batchNodeEffect(nodes: TypedNode[]): void {
    for (const node of nodes) node.effect()
  }

  static __r_batchNodeEvaluate(evaluates: Callback[]): void {
    for (const evaluate of evaluates) evaluate()
  }

  static __r_batchNodeMove(queue: MoveNodeItem[]): void {
    const visited = new Map()
    for (const { belongs, property, start } of queue) {
      const end = Math.min(
        visited.get(property) ?? property.length,
        property.length
      )
      visited.set(property, Math.min(start, end))
      for (let i = start; i < end; i++) {
        traverseIn(property[i], belongs.concat(i), (node, path) => {
          if (node instanceof TypedNode) {
            node.__r_belongs = path
          }
        })
      }
    }
  }

  static __r_createTypedNode<T extends TypedNodeConstructor>(
    TypedNodeClass: T,
    specific: TypedSpecific,
    parent: TypedNode | undefined,
    belongs: PropertyKey[],
    store: Store
  ): InstanceType<T> {
    if (specific instanceof TypedNode) {
      const existNode = getProxyRaw(specific)
      if (
        existNode.__r_store !== store &&
        getPrototypeOf(existNode) !== TypedNodeClass?.prototype
      )
        throw new FatalCrossStoreError(existNode.type)
      if (parent === existNode) throw new FatalSelfInsertError(existNode.type)
      existNode.__r_startAutoMove(parent, belongs, store)
      return existNode as InstanceType<T>
    }
    if (parent && specific.id && !TypedNode.__r_increase_id) {
      const existNode = getProxyRaw(parent.__r_node_map.get(specific.id))
      if (existNode) {
        existNode.id = specific.id
        existNode.__r_fromSpecific(specific)
        existNode.__r_startAutoMove(parent, belongs, store)
        return existNode as InstanceType<T>
      }
    }
    const prevInjectionStore = TypedNode.__r_injection_store
    const prevInjectionParent = TypedNode.__r_injection_parent
    const prevInjectionBelongs = TypedNode.__r_injection_belongs
    TypedNode.__r_injection_store = store
    TypedNode.__r_injection_parent = parent
    TypedNode.__r_injection_belongs = belongs
    try {
      return runNodeMove(() => {
        return runElementEffect(() => {
          return runNodeEvaluate(() => {
            return runNodeEffect(() => {
              const node = new TypedNodeClass(specific) as InstanceType<T>
              addNodeEffect(node)
              if (parent && parent.__r_spawned === TypedNode.__r_spawning_node) {
                parent.__r__children.push(node)
                parent.__r__children_version++
              }
              node.__r_initialized = true
              return node
            }, TypedNode.__r_batchNodeEffect)
          }, TypedNode.__r_batchNodeEvaluate)
        }, TypedNode.__r_batchElementEffect)
      }, TypedNode.__r_batchNodeMove)
    } finally {
      TypedNode.__r_injection_store = prevInjectionStore
      TypedNode.__r_injection_parent = prevInjectionParent
      TypedNode.__r_injection_belongs = prevInjectionBelongs
    }
  }

  __r_addNodeMove(belongsProperty: TypedNode[], belongs: PropertyKey[]) {
    const propertyIndex = belongsProperty.indexOf(this)
    const belongsIndex = Number(belongs.at(-1))
    const start = isNaN(belongsIndex)
      ? propertyIndex
      : Math.min(propertyIndex, belongsIndex)
    addNodeMove({
      belongs: belongs.slice(0, -1),
      property: belongsProperty,
      start
    })
  }

  @reactive('action')
  __r_applyDiff(diffs: Different[] = []) {
    this.__r_traverseElement((element) => {
      element?.model?.__r_applyDiff(diffs)
    })
  }

  __r_cleanupID() {
    const id = this.__r_id
    const ids = this.__r_ids
    const counter = this.__r_counter
    ids.delete(id)
    const [key, index] = parseID(id, this.getIncreaseKey())
    const maxIndex = Math.max(index ?? -1, counter[key] ?? -1)
    if (maxIndex <= ids.size) {
      // 小序号重置方案
      let i = maxIndex
      for (; i > 0; i--) {
        if (ids.has(this.getIncreaseID(key, i))) break
      }
      counter[key] = Math.max(i, 0)
    } else {
      // 大序号重置方案
      let maxIndex = 0
      let prefix: string
      for (const id of ids) {
        if (!id.startsWith(key) || isNaN((prefix = id.slice(key.length))))
          continue
        maxIndex = Math.max(Number(prefix), maxIndex)
      }
      counter[key] = maxIndex
    }
  }

  __r_createViewElement() {
    const store: any[] = this.__r_elements_graph
    return setNArray<ViewElement>(
      store,
      this.__r_indexes,
      (oldElement) => {
        if (oldElement) oldElement.dispose()
        return new ViewElement(this)
      },
      (oldElement) => {
        if (TypedNode.__r_auto_graph_counter > 0) oldElement.destroy()
      }
    )
  }

  __r_defaultsMerge(value: any, path: PropertyKey[]) {
    if (!TypedNode.__r_defaults_merge) return value
    const schema = this.isEntityNode()
      ? this.getPropertySchemaIn(path)
      : this.__r_entity_parent?.getPropertySchemaIn(
          this.__r_entity_belongs.concat(path)
        )
    return this.__r_store.definition.defaultsMergeWithSchema(
      value,
      schema ?? UnknownSchema,
      !this.__r_initialized
    )
  }

  __r_diff(target: any, source: any, path: PropertyKey[] = []) {
    return different(
      target,
      source,
      path,
      (target, source, path): Different[] => {
        if (
          target instanceof TypedNode &&
          target.isEntityNode() &&
          target.type === source.type &&
          target.id !== source.id
        ) {
          return target.__r_diffProperties(source, path)
        }
        return primitiveDiff(target, source, path)
      }
    ).filter((diff) => diff.path.length > 0)
  }

  __r_diffProperties(this: TRecord, source: any, path: PropertyKey[] = []) {
    const diffs: Different[] = []
    for (const key of this.__r_from_keys) {
      diffs.push(...this.__r_diff(source[key], this[key], path.concat(key)))
    }
    return diffs
  }

  __r_ensureViewElement(indexes = this.__r_indexes): ViewElement {
    const store: any[] = this.__r_elements_graph
    if (this.__r_store.__r_initialized) {
      return setNArray<ViewElement>(
        store,
        indexes,
        (oldElement, type) => {
          if (oldElement) {
            if (type !== 0) {
              oldElement.dispose(true)
              oldElement.node = this
              oldElement.model = undefined
              addElementEffect(oldElement)
            }
            return oldElement
          }
          return new ViewElement(this)
        },
        (oldElement) => {
          if (TypedNode.__r_auto_graph_counter > 0) oldElement.destroy()
        }
      )
    } else {
      store[0] ??= new ViewElement(this)
      return store[0]
    }
  }

  /**
   * 计算建模，如果是在循环场景中则每次都重建 ViewElement/ViewModel/ViewScope，
   * 否则复用已经存在的
   */
  __r_evaluate(variables?: TRecord): any {
    const element = this.__r_getEvaluateElement()
    element.scope.setLocalVariables(variables)
    this.evaluateTraverse((node) => {
      if (this.is(node)) {
        addNodeEvaluate(() => node.__r_transpileMaster(element))
        return
      }
      // 拦截非实体节点计算建模
      if (!node.isEntityNode()) return false
      const evaluateElement = node.__r_getEvaluateElement()
      addNodeEvaluate(() => node.__r_transpileMaster(evaluateElement))
    })
    return element
  }

  __r_fork(options?: ForkOptions): this {
    const index = options?.index ?? -1
    const parent = options?.parent
    const variables = options?.variables
    const proxies = new Map() // 存储每个 Node 实例的最近代理
    const indexes = this.__r_getSelfIndexes(index, parent)

    function createProxy(target: any) {
      if (!isForkNeedProxy(target)) return target
      if (target instanceof TypedNode) {
        if (target.__r_indexes === indexes) return target
        const proxy = new Proxy(target, {
          get(target: TRecord & TypedNode, key: string) {
            if (isProxyRawKey(key)) return getProxyRaw(target)
            if (key === '__r_indexes') return indexes
            if (key === '__r_parent') {
              const value = target[key]
              return proxies.get(value) ?? value
            }
            if (
              key === '__r_children' ||
              key === '__r_descendants' ||
              target.__r_from_maps?.[key]
            ) {
              return createProxy(target[key])
            }
            return target[key]
          }
        })
        proxies.set(target, proxy)
        return proxy
      }

      return new Proxy(target, {
        get(target, key) {
          if (isProxyRawKey(key)) return getProxyRaw(target)
          const value = target[key]
          if (isPrivateKey(key)) return value
          if (isForkNeedProxy(value)) return createProxy(value)
          return value
        }
      })
    }
    const proxy = createProxy(this) as this
    const element = proxy.__r_ensureViewElement(indexes)
    if (variables) {
      element.scope.setLocalVariables(variables)
    }
    return proxy
  }

  /**
   * 初始化场景下的协议填充建模，为了性能优化剪枝值为 undefined 的解析建模
   * 非初始化场景下的移动或更新场景不需要剪枝 undefined 的解析建模
   */
  __r_fromSpecific(this: TypedNode, specific: TypedSpecific): any
  __r_fromSpecific(
    this: TRecord & TypedNode,
    specific: TRecord & TypedSpecific
  ): any {
    const thisInitialized = this.__r_initialized
    const fromKeys = this.__r_from_keys
    if (fromKeys !== undefined) {
      const store = this.__r_store
      const storeInitialized = store.__r_initialized
      for (const key of fromKeys) {
        if (thisInitialized && isEqual(toJS(this[key]), specific[key])) continue
        const value = storeInitialized
          ? this.__r_defaultsMerge(specific[key], [key])
          : specific[key]
        if (!thisInitialized && value === undefined) continue
        this[key] = store.createTypedNode(value, {
          belongs: [key],
          parent: this
        })
      }
    }
    // 防止 fromNode 二次拦截给误伤拦截
    this.__r_assigned = true
  }

  __r_getEvaluateElement() {
    // 在循环开始节点或者跨树移动，需要重新创建 ViewElement
    if (
      (this.__r_indexes.length > 1 && TypedNode.__r_auto_graph_counter === 0) ||
      TypedNode.__r_moving_cross_tree
    ) {
      return this.__r_createViewElement()
    }
    return this.__r_ensureViewElement()
  }

  override __r_getEventParent() {
    return this.__r_parent ?? this.__r_store
  }

  __r_getExposedViewModels(elements = this.__r_get_view_elements()) {
    return createArrayProxy(
      elements,
      (element) => element?.model?.__r_exposed_model
    )
  }

  __r_getForkedChildren() {
    const children: TypedNode[] = []
    this.traverse((child) => {
      if (this.is(child)) return
      if (isArray(child.__r_elements_graph[0])) {
        children.push(child)
        return false
      }
    })
    return children
  }

  __r_getResetID() {
    const [key, index] = parseID(this.__r_id, this.getIncreaseKey())
    if (index > -1) return this.getIncreaseID(key, 1)
    return this.__r_id
  }

  __r_getSelfIndexes(
    index: number | number[],
    parent?: TypedNode
  ): (number | string)[] {
    const selfIndexes = parent?.__r_indexes ?? this.__r_indexes
    if (isArray(index)) return [...selfIndexes, index.join('-')]
    return index > -1 ? [...selfIndexes, index] : selfIndexes
  }

  __r_getSelfNode(indexes: (number | string)[]) {
    if (indexes === DefaultIndexes) return this
    return getElementInNArray(this.__r_elements_graph, indexes)?.node
  }

  __r_getSelfViewElement(): undefined | ViewElement {
    const indexes = this.__r_indexes
    if (indexes === DefaultIndexes && !this.__r_store.__r_initialized)
      return this.__r_elements_graph[0]
    return getElementInNArray(this.__r_elements_graph, indexes)
  }

  __r_getUnConflictID(key: string, index: number): string {
    const counter = this.__r_counter
    const count = counter[key]
    const ids = this.__r_ids
    for (let start = index + 1; start < count; start++) {
      const newID = this.getIncreaseID(key, start)
      if (!ids.has(newID)) return newID
    }
    counter[key]++
    return this.getIncreaseID(key, counter[key])
  }

  __r_getViewModels(elements = this.__r_get_view_elements()): ViewModel[] {
    return createArrayProxy(elements, (element) => element?.model) ?? []
  }

  __r_getViewScopes(elements = this.__r_get_view_elements()): ViewScope[] {
    return createArrayProxy(elements, (element) => element?.scope) ?? []
  }

  __r_isNeedFork(): boolean {
    const parent = this.__r_parent
    const graph = parent?.__r_elements_graph
    return (
      !!parent &&
      isArray(graph?.[0]) &&
      calcDepth(this.__r_elements_graph) <= calcDepth(graph)
    )
  }

  __r_isProxy(): boolean {
    return this.__r_proxy_raw !== undefined
  }

  // 设置子树ID，被移动时设置(跨树移动，非删除)
  __r_refreshSubTreeID() {
    this.__r_setSelfID(this.__r_id)
    this.__r_children.forEach((node) => node.__r_refreshSubTreeID())
  }

  __r_removeNodeFromBelongsProperty(property: any, belongs: PropertyKey[]) {
    if (property === null || typeof property !== 'object') return
    if (isArray(property)) {
      const index = property.indexOf(this)
      if (index !== -1) property.splice(index, 1)
    } else {
      const key = belongs.at(-1)
      if (property && key) delete property[key]
    }
  }

  // 重置子树ID，被删除时重置
  __r_resetSubTreeID() {
    this.__r__counter = {}
    this.__r_setSelfID(this.__r_getResetID())
    this.__r_descendants.forEach((child) =>
      child.__r_setSelfID(child.__r_getResetID())
    )
  }

  // 节点设置 ID 核心方法
  __r_setSelfID(id?: string) {
    const counter = this.__r_counter
    const ids = this.__r_ids
    const [key, index] = parseID(id, this.getIncreaseKey())
    counter[key] ??= 0
    if (id && ids.has(id)) {
      this.__r_id = this.__r_getUnConflictID(key, index)
    } else {
      if (id) {
        if (index > -1) {
          counter[key] = Math.max(counter[key], index)
        }
        this.__r_id = id
      } else {
        // 如果首次建模完成，动态插入的 specific 需要按空穴进行智能插入
        this.__r_id =
          index < counter[key] && this.__r_store.__r_initialized
            ? this.__r_getUnConflictID(key, 0)
            : this.getIncreaseID(key, ++counter[key])
      }
    }
    ids.add(this.__r_id)
  }

  __r_setSnapshotID(id?: string) {
    if (id) this.__r_snapshot_id = id
  }

  __r_shouldStartEvaluate() {
    const parent = this.__r_parent
    if (!parent) return true
    if (!parent.__r_start_evaluated && !parent.__r_initialized) return false
    if (!this.__r_initialized && parent.__r_lazy_maps?.[this.__r_belongs[0]])
      return false
    if (!this.isEntityNode()) return false
    return true
  }

  /**
   * 智能节点分析机制
   */
  __r_startAutoAnalyze(event: ObserveEvent): AnalyzeInfo | undefined {
    /**
     * ID回收应该只处理第一层的AutoCreate，在第一层的 ID 回收里会收集被覆盖对象的所有 id 和覆盖对象的 id，
     * 第一层已经处理好了此次赋值的所有 ID 回收工作，在后续的 AutoCreate 里开始自增生成新的 id，
     * 这些 id 可能是刚刚被回收了的 id。如果第二层还继续进行 ID 回收，那么就有可能会错误的回收刚刚因为自增生成的新的 id
     */
    if (TypedNode.__r_auto_counter !== 1) return

    const rawAddNodes: TypedNode[] = []
    const rawAddSpecificIds: string[] = []
    const rawRemoveNodes: TypedNode[] = []

    const recursionGetRemoveIds = (target: any) => {
      recursiveTraverse(target, (node) => {
        if (node instanceof TypedNode && this.isInSameTree(node)) {
          node = getProxyRaw(node)
          rawRemoveNodes.push(node, ...node.__r_descendants)
        }
      })
    }
    const recursionGetAddIds = (source: any) => {
      recursiveTraverse(source, (node) => {
        if (node instanceof TypedNode) {
          node = getProxyRaw(node)
          if (this.isInSameTree(node))
            rawAddNodes.push(node, ...node.__r_descendants)
        } else if (
          typeof node?.type === 'string' &&
          typeof node.id === 'string'
        ) {
          rawAddSpecificIds.push(node.id)
        }
      })
    }

    switch (event.type) {
      case 'clear': {
        recursionGetRemoveIds(event.target.get(event.key))
        break
      }
      case 'delete': {
        recursionGetRemoveIds(event.target[event.key])
        break
      }
      case 'set': {
        recursionGetRemoveIds(event.target[event.key])
        recursionGetAddIds(event.value)
        break
      }
      case 'splice': {
        const target = event.target.slice(
          event.start,
          event.start + event.deleteCount
        )
        recursionGetRemoveIds(target)
        recursionGetAddIds(event.items)
        break
      }
    }

    TypedNode.__r_transacting_ids?.push(
      ...rawAddNodes.map((node) => node.id),
      ...rawAddSpecificIds
    )

    // 去重并找到真正删除的节点集合
    const removeNodes: TypedNode[] = []
    for (const node of rawRemoveNodes) {
      // 如果待删除节点出现在待添加的节点里，说明节点实例需要发生移动，而不是删除
      if (rawAddNodes.includes(node)) continue
      // id 自增模式下不需要考虑传入的 specific id 重复的情况，实例移动模式下则需要过滤
      if (!TypedNode.__r_increase_id && rawAddSpecificIds.includes(node.id))
        continue
      if (TypedNode.__r_transacting_ids?.includes(node.id)) continue
      if (removeNodes.includes(node)) continue
      removeNodes.push(node)
    }

    // 找出自己的父亲不在移除列表里的，也就是找出移除的直接节点
    const removeRoots = removeNodes.filter(
      (node) => !removeNodes.includes(node.__r_parent)
    )

    // 找到可能被移动复用的节点，并获取其必要信息
    const addNodeInfos: AnalyzeInfo['addNodeInfos'] = []
    for (const item of [...rawAddNodes, ...rawAddSpecificIds]) {
      const node = item instanceof TypedNode ? item : this.__r_node_map.get(item)
      if (node instanceof TypedNode) {
        addNodeInfos.push([node, node.__r_root, node.__r_isNeedFork()])
      }
    }

    return {
      addNodeInfos,
      removeNodes,
      removeRoots
    }
  }

  /**
   * 智能ID回收机制
   * @param event
   */
  __r_startAutoCleanupID(event: ObserveEvent, removeNodes?: TypedNode[]) {
    if (!removeNodes?.length) return
    for (const node of removeNodes) node.__r_cleanupID()
  }

  /**
   * 智能建模机制
   * @param event
   */
  __r_startAutoCreate(event: ObserveEvent) {
    runNodeMove(
      () => {
        runNodeEvaluate(() => {
          runNodeEffect(() => {
            switch (event.type) {
              case 'set': {
                event.source.value = this.__r_store.createTypedNode(
                  this.__r_defaultsMerge(event.value, event.path),
                  { belongs: event.path, parent: this }
                )
                break
              }
              case 'splice': {
                // 处理主动操作场景，array.splice，array.push，node.remove，node.delete 等
                addNodeMove({
                  belongs: event.path,
                  property: event.target,
                  start: event.start
                })

                const startItem = event.target[event.start]
                const prefixItems = event.target.slice(0, event.start)
                event.source.items = event.items.map((item, index) => {
                  index += event.start - event.deleteCount
                  const belongs = event.path.concat(index)
                  return this.__r_store.createTypedNode(
                    this.__r_defaultsMerge(item, belongs),
                    { belongs, parent: this }
                  )
                })

                // 如果前缀元素发生变化，说明发生移动且前缀数组被抽走元素，需要修正开始位置
                if (startItem !== event.target[event.start]) {
                  let start = 0
                  for (let i = 0; i < event.start; i++) {
                    if (prefixItems[i] === event.target[start]) start++
                  }
                  event.source.start = start
                }

                // 如果出现原位移动的情况，则需要主动静默删除原位元素（区别于 startAutoMove 移动）
                let start = event.start
                for (let i = 0; i < event.source.items.length; i++) {
                  if (event.source.items[i] === event.target[start + i]) {
                    RR.undispatch(() => {
                      event.target.splice(start + i, 1)
                    })
                    start--
                  }
                }

                break
              }
            }
          }, TypedNode.__r_batchNodeEffect)
        }, TypedNode.__r_batchNodeEvaluate)
      },
      (moveItems) => {
        event.addEffect(() => {
          TypedNode.__r_batchNodeMove(moveItems)
        })
      }
    )
  }

  /**
   * 智能回收机制
   * @param event
   */
  __r_startAutoGC(event: ObserveEvent, removeRoots?: TypedNode[]) {
    if (!removeRoots?.length) return
    const removeParents = removeRoots.map((node) => node.__r_parent)
    event.addEffect(() => {
      batch(() => {
        for (const [i, node] of removeRoots.entries()) {
          const oldParent = removeParents[i]
          if (oldParent) {
            oldParent.__r__children = oldParent.__r__children.filter(
              (child) => child !== node
            )
            oldParent.__r__children_version++
          }
          /**
           * 如果父亲在回收前后发生了变化，则说明当前节点被移动抽走复用了，所以不需要回收
           * 如果父亲在回收前后是保持一致的，则说明当前节点没有被移动，所以需要发生真实回收
           */
          if (oldParent?.is(node.__r_parent)) {
            node.__r_belongs = []
            node.__r_parent = undefined
            node.dispatchEvent(new RemoveNodeEvent())
            node.__r_resetSubTreeID()
            node.dispose(true, true)
          }
        }
      })
    })
  }

  /**
   * 智能处理多维图机制
   */
  __r_startAutoGraph(
    event: ObserveEvent,
    addNodeInfos?: AnalyzeInfo['addNodeInfos']
  ) {
    const rootNodeInfos: AnalyzeInfo['addNodeInfos'] = []
    traverse(
      event.type === 'set'
        ? event.source.value
        : event.type === 'splice'
          ? event.source.items
          : null,
      (node) => {
        if (node instanceof TypedNode && !this.is(node)) {
          const existInfo = addNodeInfos?.find((info) => node.is(info[0]))
          if (existInfo) rootNodeInfos.push(existInfo)
          else rootNodeInfos.push([node, node.__r_root, node.__r_isNeedFork()])
        }
      }
    )
    if (rootNodeInfos.length === 0) return
    event.addEffect(() => {
      runElementEffect(() => {
        for (const [node, oldRoot, oldNeedFork] of rootNodeInfos) {
          try {
            // 跨树移动标记，新根如果不等于旧根
            TypedNode.__r_moving_cross_tree = !node.__r_root.is(oldRoot)
            TypedNode.__r_auto_graph_counter++
            if (node.__r_isNeedFork()) {
              // 升维、降维（除了降到 0 维）、同维
              node.__r_parent?.__r_traverseElement((element) => {
                const parent = element?.node
                if (parent) node.__r_fork({ parent }).evaluate()
              })
            } else if (oldNeedFork) {
              // 降维（降到 0 维）
              node.evaluate()
            }
          } finally {
            TypedNode.__r_moving_cross_tree = false
            TypedNode.__r_auto_graph_counter--
          }
        }
      }, TypedNode.__r_batchElementEffect)
    })
  }

  /**
   * 开启智能时代
   */
  __r_startAutomate() {
    this.addWatch(
      (event) => {
        try {
          TypedNode.__r_auto_counter++
          const analyzeInfo = this.__r_startAutoAnalyze(event)
          this.__r_startAutoCleanupID(event, analyzeInfo?.removeNodes)
          this.__r_startAutoPatch(event)
          this.__r_startAutoCreate(event)
          this.__r_startAutoGraph(event, analyzeInfo?.addNodeInfos)
          this.__r_startAutoGC(event, analyzeInfo?.removeRoots)
        } finally {
          TypedNode.__r_auto_counter--
        }
      },
      { async: false }
    )
  }

  /**
   * 智能移动机制
   * @param parent
   * @param belongs
   * @param store
   */
  __r_startAutoMove(
    newParent: TypedNode | undefined,
    newBelongs: PropertyKey[],
    newStore: Store
  ) {
    if (!newParent) return

    const oldParent = this.__r_parent
    const oldBelongs = this.__r_belongs

    if (
      is(newParent, oldParent) &&
      newBelongs.join(',') === oldBelongs.join(',')
    ) {
      return
    }

    const newRoot = newParent.__r_root
    const newBelongsProperty = getIn(newParent, newBelongs.slice(0, -1))
    const oldRoot = this.__r_root
    const oldBelongsProperty = getIn(oldParent, oldBelongs.slice(0, -1))
    const isSameRoot = is(newRoot, oldRoot)

    // 处理被动操作场景，在节点移动过程中 __r_removeNodeFromBelongsProperty
    if (isArray(oldBelongsProperty))
      this.__r_addNodeMove(oldBelongsProperty, oldBelongs)
    if (isArray(newBelongsProperty))
      this.__r_addNodeMove(newBelongsProperty, newBelongs)

    if (!isSameRoot) this.__r_cleanupID()

    this.__r_belongs = newBelongs
    this.__r_parent = newParent
    this.__r_store = newStore

    if (!isSameRoot) this.__r_refreshSubTreeID()

    if (oldParent) {
      oldParent.__r__children = oldParent.__r__children.filter(
        (child) => child !== this
      )
      oldParent.__r__children_version++
    }
    newParent.__r__children.push(this)
    newParent.__r__children_version++

    // 移动不会改动子树，所以需要递归触发生命周期
    this.dispose(true)

    // 如果正在事务过程当中则不触发节点的隐式删除
    if (!TypedNode.__r_transacting_ids) {
      batch(
        () =>
          this.__r_removeNodeFromBelongsProperty(oldBelongsProperty, oldBelongs),
        {
          origin: TypedNode.__r_origin
        }
      )
    }

    this.dispatchEvent(isSameRoot ? new MoveNodeEvent() : new AddNodeEvent())
    // 移动不会改动子树，所以需要递归触发生命周期
    this.effect(true)
  }

  /**
   * 智能更新机制
   * @param event
   */
  __r_startAutoPatch(event: ObserveEvent) {
    if (this.isEntityNode()) {
      const path = event.path
      if (event.type === 'set') {
        const oldValue = event.target[event.key]
        event.addEffect(() => {
          this.__r_applyDiff(this.__r_diff(oldValue, event.source.value, path))
        })
      } else if (event.type === 'delete') {
        event.addEffect(() => {
          this.__r_applyDiff([
            {
              path,
              type: 'delete'
            }
          ])
        })
      } else {
        event.addEffect(() => {
          this.__r_applyDiff([
            {
              newValue: getIn(this, path),
              path,
              type: 'set'
            }
          ])
        })
      }
    } else {
      event.addEffect(() => {
        const target = this.__r_entity_parent
        if (target) {
          const belongs = this.__r_entity_belongs
          if (belongs.length === 0) return
          target.__r_applyDiff([
            {
              newValue: getIn(target, belongs),
              path: belongs,
              type: 'set'
            }
          ])
        }
      })
    }
  }

  __r_startEvaluate() {
    const element = this.__r_ensureViewElement()
    if (this.__r_shouldStartEvaluate()) {
      addNodeEvaluate(() => this.__r_transpileMaster(element))
      this.__r_start_evaluated = true
    }
  }

  __r_transpile(target: any): any {
    if (isTerminateValue(target) || isSkipCreateTypedModel(target)) {
      return target
    }
    if (target instanceof TypedNode) {
      return target.__r_transpileSlave()
    }
    if (isArray(target)) {
      return target.map((child) => this.__r_transpile(child))
    }
    if (getPrototypeOf(target) !== objectPrototype) {
      return target
    }
    const obj: TRecord = {}
    for (const key of getMemoizedKeys(target)) {
      if (isPrivateKey(key)) continue
      obj[key] = this.__r_transpile(target[key])
    }
    return obj
  }

  __r_transpileMaster(element: ViewElement = this.__r_getEvaluateElement()) {
    if (element.model) return element.model
    /**
     * transpileMaster 的时候也要 transform，spawn MustacheNode 的时候走的就是 transpileMaster，
     * 这里不用 instanceof ViewModel，因为已经在 transform 里做了判断
     */
    return this.transform(this.transpile(element.scope))
  }

  __r_transpileSlave(element: ViewElement = this.__r_getEvaluateElement()): any {
    try {
      if (this.isEntityNode()) {
        addNodeEvaluate(() => {
          element.model ??= this.__r_transpileMaster(element)
        })
      }
      return this.transpileSlave(element.scope)
    } catch (error) {
      this.raise(error as Error)
      return
    }
  }

  __r_traverseElement(visitor: (element?: ViewElement) => void) {
    traverseElementsInNArray(this.__r_elements_graph, (element) =>
      visitor(element)
    )
  }

  /**
   * 遍历当前实体节点的所有直接从节点，如果当前是从节点则不遍历
   * eg:
   *
   * - block1(entity)
   *   - mappingFunction1
   *     - js1
   *   - text1(entity)
   *     - mappingFunction2
   *       - js2
   *     - js3
   *   - js4
   *
   * block1.__r_traverseSlaveNode(fn) => fn(mappingFunction1), fn(js1), fn(js4)
   */
  __r_traverseSlaveNode(visitor: (node: TypedNode) => void): void {
    if (!this.isEntityNode()) return
    this.traverse((child) => {
      if (this.is(child)) return
      if (child.isEntityNode()) return false
      visitor(child)
    })
  }

  /**
   * 监听当前节点所有响应式属性变化
   * @param observer 监听器
   * @param options 监听参数
   */
  addWatch(
    observer: Observer,
    options?: EffectOptions & ObserveOptions
  ): Disposer {
    return this.addObserve(
      this,
      (event) => {
        if (this.is(closest(event.target, isTypedNode))) observer(event)
      },
      options
    )
  }

  /**
   * 给当前节点的指定路径的子属性向后追加指定节点(只对数组场景生效)，如果插入失败则返回 undefined
   * @param specifics
   * @param belongs
   */
  append<T extends TypedNode = TypedNode>(
    specifics: TypedSpecific,
    belongs?: PropertyKey[]
  ): T | undefined
  append<T extends TypedNode = TypedNode>(
    specifics: TypedSpecific[],
    belongs?: PropertyKey[]
  ): T[]
  append<T extends TypedNode = TypedNode>(
    specifics: TypedSpecific | TypedSpecific[],
    belongs?: PropertyKey[]
  ): T | T[] | undefined
  append(specifics: TypedSpecific | TypedSpecific[], belongs = ['children']) {
    const property = getIn(this, belongs)
    if (isArray(property)) {
      if (isArray(specifics)) {
        const length = property.length
        property.push(...specifics)
        return property.slice(length)
      } else {
        property.push(specifics)
        return property.at(-1)
      }
    } else if (isArray(specifics)) return []
  }

  /**
   * 包装函数内所有建模过程填充默认值
   */
  batchDefaultsMerge<T = any>(
    callback: Callback<T>,
    deepDefaultsMerge?: boolean
  ): T {
    return TypedNode.__r_batchDefaultsMerge(callback, deepDefaultsMerge)
  }

  /**
   * 包装函数内所有建模过程都延迟计算
   */
  batchEvaluate<T = any>(callback: Callback<T>): T {
    return runNodeEvaluate(callback, TypedNode.__r_batchNodeEvaluate)
  }

  /**
   * 判断当前节点是否包含指定节点
   * @param node
   */
  contains(node?: TypedNode): boolean {
    if (!node) return false
    return node === this || this.__r_descendants.includes(node)
  }

  /**
   * 清理副作用 默认不递归
   * @param recursion
   */
  override dispose(recursion = false, disposeElement = false): void {
    if (disposeElement) this.__r_traverseElement((element) => element?.dispose())
    if (recursion) {
      this.__r_children.forEach((child) =>
        child.dispose(recursion, disposeElement)
      )
    }
    super.dispose()
  }

  /**
   * 触发副作 默认不递归
   * @param recursion
   */
  override effect(recursion = false, effectElement = false) {
    if (effectElement) this.__r_traverseElement((element) => element?.effect())
    if (recursion) {
      this.__r_children.forEach((child) => child.effect(recursion, effectElement))
    }
    return super.effect()
  }

  /**
   * 视图建模
   * @example
   * class MappingFunctionNode extends TypedNode {
   *   override transpile(scope){
   *     return (item,index)=>{
   *       return this.return?.fork(index).evaluate?.({
   *         item,
   *         i:index
   *       })
   *     }
   *   }
   * }
   * class EventNode extends TypedNode {
   *   override transpile(){
   *      return ()=>this.action?.evaluate?.()
   *    }
   *  }
   * @param variables 临时变量
   */
  evaluate(path: PropertyKey[], variables?: TRecord): any
  evaluate(variables: TRecord): any
  evaluate(): any
  evaluate(arg1?: any, arg2?: any): any {
    return runElementEffect(() => {
      return runNodeEvaluate(() => {
        if (isArray(arg1)) {
          return traverseTo(this.getInProperty(arg1), (node) => {
            if (node instanceof TypedNode) return node.evaluate(arg2)
            return node
          })
        }
        if (this.isEntityNode()) {
          return this.__r_transpileSlave(this.__r_evaluate(arg1))
        }
        return this.__r_transpileSlave()
      }, TypedNode.__r_batchNodeEvaluate)
    }, TypedNode.__r_batchElementEffect)
  }

  /**
   * 建模递归器，可以重载
   * @param visitor
   */
  evaluateTraverse(visitor: (node: TypedNode) => boolean | void) {
    if (visitor(this) === false) return
    for (const child of this.__r_children) {
      child.evaluateTraverse(visitor)
    }
  }

  /**
   * 从根节点开始查询节点
   * @param filter 过滤函数
   */
  find<T extends TypedNode>(filter?: Filter<T>): Result<T> | undefined
  /**
   * 从根节点开始查询节点
   * @param id 节点 ID
   */
  find<T extends TypedNode>(id?: string): T | undefined
  find(arg1?: any): any {
    if (typeof arg1 === 'string') {
      const node = this.__r_node_map.get(arg1) ?? this.__r_unique_node_map.get(arg1)
      if (node) return node
    }
    if (typeof arg1 === 'function') {
      for (const node of this.__r_nodes) {
        if (arg1(node)) return node
      }
    }
  }

  /**
   * 从根节点开始查询节点集合
   * @param filter 过滤函数
   */
  findAll<T extends TypedNode>(filter?: Filter<T>): T[]
  findAll(arg1?: any): any {
    const rootNodes = this.__r_root.__r_descendants
    return arg1 === undefined ? rootNodes : rootNodes.filter(arg1)
  }

  /**
   * 创建 TypedNode 分叉，主要用于循环场景
   * 注意：分叉出来的所有子树 TypedNode 如果继续分叉，都会逐级扩散
   * @example
   * class MappingFunctionNode extends TypedNode {
   *   override transpile(scope){
   *     return (item,index)=>{
   *       return this.return?.fork(index).evaluate?.({
   *         item,
   *         i:index
   *       })
   *     }
   *   }
   * }
   * @param index 分叉索引
   * @param variables 分叉局部变量
   */
  fork(index: number | number[] = -1, variables?: TRecord): this {
    return this.__r_fork({ index, variables })
  }

  /**
   * 导入快照，用户必须重载定制导入规则，否则不知道如何处理插入逻辑
   */
  fromSnapshot(root: TypedNode): void
  fromSnapshot(_arg1: any): void {
    throw new FatalAssertOverrideError(this, 'fromSnapshot')
  }

  /**
   * 返回相对根的所属路径
   */
  getAddress() {
    return this.__r_address
  }

  /**
   * 获取相对根节点的所属路径字符串
   */
  getAddressSource() {
    return this.__r_address_source
  }

  /**
   * 获取相对父节点的所属路径
   */
  getBelongs() {
    return this.__r_belongs
  }

  /**
   * 获取当前 TypedNode 所在父节点的最后一个容器属性引用
   */
  getBelongsProperty() {
    return this.__r_belongs_property
  }

  /**
   * 获取当前节点所在父节点的 schema
   * @returns
   */
  getBelongsSchema(): TypedSchema {
    return this.__r_parent?.getPropertySchemaIn(this.__r_belongs) ?? UnknownSchema
  }

  /**
   * 获取相对父节点的所属路径字符串
   */
  getBelongsSource() {
    return this.__r_belongs_source
  }

  /**
   * 获取子节点集合
   *
   * @example
   * [ElementNode block1] {
   *   props: {
   *     title: [JSExpressionNode js1],
   *   },
   *   children: [
   *     [ElementNode text1],
   *     [ElementNode text2],
   *   ]
   * }
   *
   * block1.getChildren() ==> [js1, text1, text2]
   *
   * // block1.children 属性是 ElementNode 的自定义属性，非 TypedNode 基类属性
   * block1.children => [text1, text2]
   */
  getChildren<T extends TypedNode[] = TypedNode[]>(): T {
    return this.__r_children as T
  }

  /**
   * 获取依赖的节点集合，用于实现依赖收集，用户需要重载
   */
  getDependencies(): Dependency[] {
    return []
  }

  /**
   * 获取依赖的变量名，用户需要重载
   */
  getDependencyVariables(): string[] {
    return []
  }

  /**
   * 获取相对根节点的深度
   */
  getDepth(): number {
    return this.__r_parents.length
  }

  /**
   * 获取子孙集合
   */
  getDescendants<T extends TypedNode[] = TypedNode[]>(): T {
    return this.__r_descendants as T
  }

  /**
   * 获取实体路径
   * @returns
   */
  getEntityBelongs() {
    return this.__r_entity_belongs
  }

  /**
   * 获取实体父亲节点
   */
  getEntityParent<T extends TypedNode = TypedNode>(): T | undefined {
    return this.__r_entity_parent as T | undefined
  }

  /**
   * 获取当前节点暴露的类型定义
   * @param path 指定路径查找
   */
  getExposedTypedSchema(path: PropertyKey[] = []): TypedSchema {
    if (path.length === 0) return this.__r_exposed_schema
    return this.__r_store.definition.getInWithSchema(this.__r_exposed_schema, path)
  }

  /**
   * 获取视图模型开放 API，主要用于对外暴露变量
   */
  getExposedViewModel<T extends ViewModel = ViewModel>():
    | (T & T[])
    | undefined {
    const model = this.__r_getSelfViewElement()?.model
    if (!model && this.__r_indexes === DefaultIndexes) return undefined
    return createArrayLike(
      model?.__r_exposed_model,
      this.__r_getExposedViewModels()
    )
  }

  /**
   * 获取当前 TypedNode 所在 Fork 层级索引集合
   */
  getForkIndexes(): (number | string)[] {
    return this.__r_indexes
  }

  /**
   * 获取自增ID(可重载定制)
   * @param increaseKey 自增基础 key
   * @param increaseIndex 自增索引，为 -1 时代表不存在重复
   */
  getIncreaseID(increaseKey: string, increaseIndex: number) {
    if (increaseIndex < 1) return increaseKey
    return increaseKey + increaseIndex
  }

  /**
   * 获取自增前缀 key(可重载定制)
   * @returns
   */
  getIncreaseKey() {
    return this.type
  }

  /**
   * 基于路径查找属性
   * @param path
   */
  getInProperty(path: PropertyKey[] = []) {
    return getIn(this, path)
  }

  /**
   * 获取可迭代的所有属性名，序列化核心方法，如果想要定制序列化，可以重载该方法
   */
  getIterableKeys(): PropertyKey[] {
    return ['type', 'id', ...(this.__r_from_keys ?? [])]
  }

  /**
   * 获取当前节点的定位路径
   * @returns
   */
  getLocation() {
    return this.__r_location
  }

  /**
   * 获取当前节点在所属树的顺序
   */
  getOrder() {
    return this.__r_order
  }

  /**
   * 获取父节点
   */
  getParent<T extends TypedNode = TypedNode>(): T | undefined {
    return this.__r_parent as T | undefined
  }

  /**
   * 获取父节点集合，顺序，从当前 TypedNode 的父节点开始，一直到根节点
   */
  getParents<T extends TypedNode[] = TypedNode[]>(): T {
    return this.__r_parents as T
  }

  /**
   * 获取当前 TypedNode 所在父节点的数组容器引用的索引，如果容器属性不是数组，返回 -1
   */
  getPropertyIndex(): number {
    const property = this.getBelongsProperty()
    return isArray(property) ? property.indexOf(this) : -1
  }

  getPropertySchemaIn(path: PropertyKey[] = []): TypedSchema {
    return (
      this.__r_store.definition.__r_injectPathNode(
        () => this.__r_typed_definition?.getPropertySchemaIn(path),
        path,
        this
      ) ?? UnknownSchema
    )
  }

  /**
   * 获取根节点
   */
  getRoot<T extends TypedNode = TypedNode>(): T {
    return this.__r_root as T
  }

  /**
   * 获取当前暴露的视图模型
   */
  getSelfExposedViewModel<T extends ViewModel>(): T | undefined {
    return this.getSelfViewModel()?.__r_exposed_model as T | undefined
  }

  /**
   * 获取当前视图模型
   */
  getSelfViewModel<T extends ViewModel>(): T | undefined {
    return this.__r_getSelfViewElement()?.model as T | undefined
  }

  /**
   * 获取当前视图作用域
   */
  getSelfViewScope(): undefined | ViewScope {
    return this.__r_getSelfViewElement()?.scope
  }

  /**
   * 获取快照ID，如果不存在默认为 UniqueID
   */
  getSnapshotID() {
    return this.__r_snapshot_id ?? this.__r_unique_id
  }

  /**
   * 获取状态中心
   */
  getStore<T extends Store = Store>(): T {
    return this.__r_store as T
  }

  /**
   * 获取类型
   */
  getType(): string {
    return this.__r_type
  }

  /**
   * 获取类型定义
   */
  getTypedDefinition<T extends TypedDefinition = TypedDefinition>():
    | T
    | undefined {
    return this.__r_typed_definition as T | undefined
  }

  /**
   * 获取 ADT Schema 定义
   * @param path 指定路径查找
   */
  getTypedSchema(path: PropertyKey[] = []): TypedSchema {
    if (path.length === 0) return this.__r_schema
    return this.__r_store.definition.getInWithSchema(this.__r_schema, path)
  }

  /**
   * 获取类型作用域
   */
  getTypedScope() {
    return this.__r_typed_scope
  }

  /**
   * 获取当前 TypedNode 所在作用域的可访问变量类型定义
   */
  getTypedVariables() {
    return this.__r_typed_scope.variables
  }

  /**
   * 获取当前节点的全局稳定的唯一 ID
   */
  getUniqueID() {
    return this.__r_unique_id
  }

  /**
   * 获取视图元素
   * 注意：返回结果是一个 ArrayLike 结构
   */
  getViewElement() {
    return createArrayLike(
      this.__r_getSelfViewElement(),
      this.__r_get_view_elements()
    )
  }

  /**
   * 获取视图模型
   * 注意：返回结果是一个 ArrayLike 结构
   */
  getViewModel<T extends ViewModel = ViewModel>(): (T & T[]) | undefined {
    const model = this.__r_getSelfViewElement()?.model
    if (!model && this.__r_indexes === DefaultIndexes) return undefined
    return createArrayLike(model, this.__r_getViewModels()) as any
  }

  /**
   * 获取视图作用域
   * 注意：返回结果是一个 ArrayLike 结构
   */
  getViewScope() {
    return createArrayLike(
      this.__r_getSelfViewElement()?.scope,
      this.__r_getViewScopes()
    )
  }

  override hasOwnProperty(key: string): boolean {
    return this.getIterableKeys().includes(key)
  }

  /**
   * 给当前节点的后方插入指定节点(只对数组场景生效)，如果插入失败则返回 undefined
   * @param specifics
   */
  insertAfter<T extends TypedNode = TypedNode>(
    specifics: TypedSpecific
  ): T | undefined
  insertAfter<T extends TypedNode = TypedNode>(specifics: TypedSpecific[]): T[]
  insertAfter<T extends TypedNode = TypedNode>(
    specifics: TypedSpecific | TypedSpecific[]
  ): T | T[] | undefined
  insertAfter(specifics: TypedSpecific | TypedSpecific[]) {
    const property = this.__r_belongs_property
    if (isArray(property)) {
      const index = property.indexOf(this)
      if (index !== -1) {
        if (isArray(specifics)) {
          property.splice(index + 1, 0, ...specifics)
          return property.slice(index + 1, index + 1 + specifics.length)
        } else {
          property.splice(index + 1, 0, specifics)
          return property[index + 1]
        }
      }
    } else if (isArray(specifics)) return []
  }

  /**
   * 给当前节点的前方插入指定节点(只对数组场景生效)，如果插入失败则返回 undefined
   * @param specifics
   */
  insertBefore<T extends TypedNode = TypedNode>(
    specifics: TypedSpecific
  ): T | undefined
  insertBefore<T extends TypedNode = TypedNode>(specifics: TypedSpecific[]): T[]
  insertBefore<T extends TypedNode = TypedNode>(
    specifics: TypedSpecific | TypedSpecific[]
  ): T | T[] | undefined
  insertBefore(specifics: TypedSpecific | TypedSpecific[]) {
    const property = this.__r_belongs_property
    if (isArray(property)) {
      const index = property.indexOf(this)
      if (index !== -1) {
        if (isArray(specifics)) {
          property.splice(index, 0, ...specifics)
          return property.slice(index, index + specifics.length)
        } else {
          property.splice(index, 0, specifics)
          return property[index]
        }
      }
    } else if (isArray(specifics)) return []
  }

  /**
   * 判断目标对象是否是当前节点，因为考虑到有些场景 TypedNode 可能会被 Proxy 包装，引用会发生变化
   * 所以需要一个特殊的方法来判断目标节点是否是当前节点
   * @param target 目标对象
   */
  is(target?: any): target is this {
    return is(this, target)
  }

  /**
   * 是否为实体节点(非表达式，函数之类的逻辑表达节点)
   */
  isEntityNode(): boolean {
    return this.__r_store.plugin.hasViewModelClass(this.type)
  }

  /**
   * 是否为携带 ViewModel 的视图节点
   */
  isEntityViewNode(): boolean {
    return this.isEntityNode() && this.isViewNode()
  }

  /**
   * 判断指定节点是否同树
   * @param node
   */
  isInSameTree(node?: TypedNode) {
    return this.__r_root.is(node?.__r_root)
  }

  /**
   * 是否为根节点
   */
  isRoot() {
    return this === this.__r_root
  }

  /**
   * 是否为视图节点(带渲染组件的节点)
   */
  isViewNode(): boolean {
    return this.__r_store.plugin.hasViewComponentClass(this.type)
  }

  /**
   * 给当前节点的指定路径的子属性向前追加指定节点(只对数组场景生效)，如果插入失败则返回 undefined
   * @param specifics
   * @param belongs
   */
  prepend<T extends TypedNode = TypedNode>(
    specifics: TypedSpecific,
    belongs?: PropertyKey[]
  ): T | undefined
  prepend<T extends TypedNode = TypedNode>(
    specifics: TypedSpecific[],
    belongs?: PropertyKey[]
  ): T[]
  prepend<T extends TypedNode = TypedNode>(
    specifics: TypedSpecific | TypedSpecific[],
    belongs?: PropertyKey[]
  ): T | T[] | undefined
  prepend(specifics: TypedSpecific | TypedSpecific[], belongs = ['children']) {
    const property = getIn(this, belongs)
    if (isArray(property)) {
      if (isArray(specifics)) {
        property.unshift(...specifics)
        return property.slice(0, specifics.length)
      } else {
        property.unshift(specifics)
        return property[0]
      }
    } else if (isArray(specifics)) return []
  }

  /**
   * 移除当前节点
   */
  remove(): void {
    try {
      this.__r_removeNodeFromBelongsProperty(
        this.__r_belongs_property,
        this.__r_belongs
      )
    } catch {}
  }

  /**
   * 渲染当前节点
   * @returns virtual dom
   */
  render() {
    return this.__r_store.render?.(this)
  }

  /**
   * 使用指定节点替换当前节点
   * @param specifics
   */
  replace(specific: TypedSpecific): void {
    const property = this.__r_belongs_property
    const belongs = this.__r_belongs
    const key = belongs.at(-1)
    if (!property || key == null) return
    property[key] = specific
  }

  /**
   * 替换搜索结果
   * @param replaceText 替换文本
   * @param result 搜索结果
   */
  @reactive('action')
  replaceSearchResult(replaceText: string, result: SearchResultItem[] = []) {
    for (const item of result) {
      const { matches, node, path, replace } = item
      const fullText = node.getInProperty(path)
      if (matches.length === 0 || replace === false) continue
      const sorted = [...matches].toSorted((a, b) => a.start - b.start)
      let newText = ''
      let cursor = 0
      for (const match of sorted) {
        newText += fullText.slice(cursor, match.start) + replaceText
        cursor = match.end
      }
      newText += fullText.slice(cursor)
      node.setInProperty(path, newText)
    }
  }

  /**
   * 全文搜索 API，用于在模型树中搜索指定文本
   * @param this
   * @param options
   * @returns
   */
  search(this: TRecord & TypedNode, options: SearchOptions = {}) {
    const result: SearchResultItem[] = []
    const current = this
    function match(target: string, options: SearchOptions): SearchMatch[] {
      const { caseSensitive, searchText, useRegex, wholeWord } = options
      if (!searchText) return []
      let flags = 'g'
      if (!caseSensitive) flags += 'i'
      let pattern = searchText
      if (useRegex) {
        try {
          new RegExp(pattern)
        } catch {
          return []
        }
      } else {
        pattern = pattern.replaceAll(/[$()*+.?[\\\]^{|}]/g, String.raw`\$&`)
      }
      if (wholeWord) {
        pattern = String.raw`\b${pattern}\b`
      }
      let regex: RegExp
      try {
        regex = new RegExp(pattern, flags)
      } catch {
        return []
      }
      const lines = target.split(/\r?\n/)
      const matches: SearchMatch[] = []
      let lineStartOffset = 0
      for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
        const line = lines[lineIdx]
        regex.lastIndex = 0
        let matchResult: null | RegExpExecArray
        while ((matchResult = regex.exec(line)) !== null) {
          matches.push({
            column: matchResult.index + 1,
            end: lineStartOffset + matchResult.index + matchResult[0].length,
            line: lineIdx + 1,
            start: lineStartOffset + matchResult.index,
            text: matchResult[0]
          })
          if (matchResult.index === regex.lastIndex) {
            regex.lastIndex++
          }
        }
        // 计算下一个 line 的起始 offset
        // 注意：split(/\r?\n/) 只会去掉分隔符，不会保留，所以每行后面要加1（假设是 \n），但如果原始字符串是 \r\n，需要加2
        // 这里用 target.indexOf(line, lineStartOffset) + line.length 可以更精确地找到下一个行的起点
        lineStartOffset += line.length
        // 如果不是最后一行，补上分隔符长度
        if (lineIdx < lines.length - 1) {
          // 检查原始分隔符长度
          const nextChar = target[lineStartOffset]
          if (nextChar === '\r' && target[lineStartOffset + 1] === '\n') {
            lineStartOffset += 2
          } else if (nextChar === '\n' || nextChar === '\r') {
            lineStartOffset += 1
          }
        }
      }
      return matches
    }
    function traverse(target: any, path: PropertyKey[] = []) {
      if (isTerminateValue(target)) {
        if (typeof target === 'string') {
          const matches = match(target, options)
          if (matches.length > 0) {
            result.push({
              id: uid(),
              matches,
              node: current,
              path,
              previewText: target,
              searchText: options.searchText!
            })
          }
        }
        return
      }
      if (target instanceof TypedNode) {
        result.push(...target.search(options))
        return
      }
      if (isArray(target)) {
        target.forEach((item, index) => traverse(item, path.concat(index)))
      } else if (getPrototypeOf(target) === objectPrototype) {
        for (const key of keys(target)) {
          traverse(target[key], path.concat(key))
        }
      }
    }
    if (!options.searchText) return []
    for (const key of this.getIterableKeys()) {
      if (key === 'id' || key === 'type') continue
      traverse(this[key], [key])
    }
    return result
  }

  /**
   * 按照指定路径给当前模型赋值
   * @param path 指定路径
   * @param value 指定值
   * @param increaseID 是否自增 ID，默认自增
   *
   * 在 increaseID:false 的场景下，使用带有相同 id 的 json 发生自动建模，
   * 会因为相同的节点引用还存在建模流程的局部变量中，无法被移动逻辑处理，
   * 导致赋值到主树上之后出现相同的 id 的节点，这是预期的特殊场景
   */
  setInProperty(path: PropertyKey[] = [], value: any, increaseID = true) {
    const prevIncreaseID = TypedNode.__r_increase_id
    try {
      TypedNode.__r_increase_id = increaseID
      setIn(this, path, value)
    } finally {
      TypedNode.__r_increase_id = prevIncreaseID
    }
  }

  /**
   * 设置监听器，监听当前节点所有响应式属性变化，重复调用会自动销毁上一个同 key watch
   * @param key 唯一键
   * @param observer 监听器
   * @param options 监听参数
   */
  setWatch(
    key: string,
    observer: Observer,
    options?: EffectOptions & ObserveOptions
  ): Disposer {
    return this.setObserve(
      key,
      this,
      (event) => {
        if (closest(event.target, isTypedNode) === this) observer(event)
      },
      options
    )
  }

  /**
   * 创建虚拟子节点
   * @param belongs
   * @param specific
   */
  @reactive('action')
  spawn<T extends TypedNode>(
    belongs: PropertyKey[] = [],
    specific?: TypedSpecific
  ): T | undefined {
    if (!specific) return
    const getOptions = (): { belongs: PropertyKey[]; parent: TypedNode } => {
      const node = getIn(this, belongs)
      if (node instanceof TypedNode)
        return {
          belongs: node.__r_belongs,
          parent: node.getParent() ?? this
        }

      for (let i = belongs.length; i >= 0; i--) {
        const _belongs = belongs.slice(0, i)
        const node = getIn(this, _belongs)
        if (node instanceof TypedNode)
          return {
            belongs: belongs.slice(i),
            parent: node
          }
      }

      return {
        belongs,
        parent: this
      }
    }

    const options = getOptions()
    const parent = options.parent
    const counter = assign({}, parent.__r_counter)
    const ids = new Set(parent.__r_ids)

    TypedNode.__r_spawning_node = true
    let node = this.__r_store.createTypedNode(specific, options) as TypedNode
    TypedNode.__r_spawning_node = false

    assign(parent.__r_counter, counter)
    parent.__r_ids.clear()
    for (const id of ids) parent.__r_ids.add(id)

    const entityBelongs = this.isEntityNode()
      ? belongs
      : this.__r_entity_belongs.concat(belongs)

    if (this.__r_indexes !== DefaultIndexes) {
      node = node.__r_fork({ parent: this })
      // 在循环体中需要基于不同索引建模，否则取值会异常
      node.evaluate()
    }

    return new Proxy(node, {
      get: (target: TRecord, key) => {
        if (isProxyRawKey(key)) return getProxyRaw(target)
        if (key === '__r_belongs') return belongs
        if (key === '__r_belongs_property') return
        if (key === '__r_entity_belongs') return entityBelongs
        if (key === 'dispose') return () => target.dispose(true, true)
        return target[key]
      }
    }) as any
  }

  [Symbol.iterator](this: this & TRecord): TIterator<TRecord> {
    const entries = this.getIterableKeys().map((key) => [key, this[key]])
    return entries[Symbol.iterator]()
  }

  testPropertyIn(path: PropertyKey[] = [], value: any): boolean {
    return this.validatePropertyIn(path, value).length === 0
  }

  /**
   * 序列化节点
   * @param visitor 递归转换器，可定制序列化规则
   */
  toJS(visitor?: typeof toJS.visitor): TypedSpecific {
    return toJSByIterableKeys(this, visitor)
  }

  /**
   * 序列化节点
   */
  toJSON(visitor?: typeof toJSON.visitor): TypedSpecific {
    return toJSONByIterableKeys(this, visitor)
  }

  /**
   * 生成快照，基于 toJSON，相比于 toJSON
   * 用户可以重载扩展携带 __r_snapshot_id 等额外信息等
   */
  toSnapshot(): SnapshotSpecificLike {
    return this.toJSON((value, toJSON) => {
      if (value instanceof TypedNode) {
        if (value === this) {
          const snapshot: TRecord = {
            __r_order: this.getOrder(),
            __r_snapshot_id: this.getSnapshotID()
          }
          for (const key of value.getIterableKeys()) {
            snapshot[key] = toJSON(value[key as keyof TypedNode])
          }
          return snapshot
        } else return value.toSnapshot()
      } else return toJSON(value)
    }) as SnapshotSpecificLike
  }

  /**
   * 基于父模型类型定义对当前模型做类型转换
   * 需要注意：Store 如果配置 disableTypeTransformer，将不会生效
   * @param value
   * @returns
   */
  transform(value: any) {
    if (this.__r_store.options.disableTypeTransformer) return value
    if (value instanceof ViewModel && this.is(value.__r_node)) return value
    const parent = this.__r_entity_parent
    if (parent) return parent.transformPropertyIn(this.__r_entity_belongs, value)
    return value
  }

  /**
   * 基于指定路径的 Schema 进行类型转换
   * @param path 路径
   * @param value 转换值
   */
  transformPropertyIn(path: PropertyKey[] = [], value: any) {
    if (this.__r_store.options.disableTypeTransformer) return value
    const definition = this.__r_typed_definition
    if (definition) {
      return this.__r_store.definition.__r_injectPathNode(
        () => definition.transformPropertyIn(path, value),
        path,
        this
      )
    }
    return value
  }

  /**
   * 创建视图模型(可重载)，调用该方法并不会影响模型树层级，绝对无副作用
   * @example
   * class JSExpressionNode extends TypedNode {
   *   override transpile(scope){
   *    let computed
   *    this.addEffect(()=>scope.addReaction(
   *      ()=>computed = this.run(),
   *      (value)=>Path.setIn(scope.model,this.getBelongs())
   *    ),{
   *      effectImmediately:true
   *    })
   *    return computed
   *   }
   * }
   * @param scope 建模过程中传入的作用域模型，这个 scope 只有在同步逻辑中才是稳定的，
   * 在异步逻辑中拿到的 scope 可能会因为闭包缓了旧的 scope，
   * 可以使用 this.getSelfViewScope() 获取最新的 scope
   */
  transpile<T = any>(this: TypedNode, scope: ViewScope): T
  transpile(this: TypedNode, scope: ViewScope): any
  transpile(this: TRecord & TypedNode, scope: ViewScope): any {
    const ViewModelClass = this.__r_store.plugin.getViewModelClass(this.type)
    if (ViewModelClass) {
      return suspend(
        () => ViewModel.__r_createViewModel(ViewModelClass, this, scope.element),
        (error) => this.raise(error)
      )
    } else {
      const obj: TRecord = {}
      const keys = this.__r_from_keys ?? []
      for (const key of keys) {
        obj[key] = this.__r_transpile(this[key])
      }
      return obj
    }
  }

  /**
   * 创建从属性模型(可重载)，调用该方法并不会影响模型树层级，绝对无副作用
   * @example
   * class ElementNode extends TypedNode {
   *   override transpileSlave(scope){
   *      return this.render()
   *   }
   * }
   * @param scope 建模过程中传入的作用域模型
   */
  transpileSlave(scope: ViewScope) {
    return this.__r_transpileMaster(scope.element)
  }

  /**
   * 向下递归遍历所有子孙节点(包含自身)，主动返回 false 将中断当前分支的遍历
   * @param visitor
   */
  traverse<T extends TypedNode>(
    this: TRecord & TypedNode,
    visitor: (node: T) => boolean | void
  ): void {
    if (visitor(this) === false) return
    for (const child of this.__r_children) {
      child.traverse(visitor)
    }
  }

  /**
   * 向下递归子属性，到 TypedNode 为止
   * @param this
   * @param visitor
   */
  traverseProperties(
    this: TRecord & TypedNode,
    visitor: (value: any, path: PropertyKey[]) => void
  ) {
    function traverse(target: any, path: PropertyKey[] = []) {
      visitor(target, path)
      if (isTerminateValue(target)) return
      if (target instanceof TypedNode) return
      if (isArray(target)) {
        target.forEach((item, index) => traverse(item, path.concat(index)))
      } else if (getPrototypeOf(target) === objectPrototype) {
        for (const key of keys(target)) {
          traverse(target[key], path.concat(key))
        }
      }
    }
    for (const key of this.getIterableKeys()) {
      traverse(this[key], [key])
    }
  }

  validatePropertyIn(path: PropertyKey[] = [], value: any): Error[] {
    const definition = this.__r_typed_definition
    if (!definition) return []
    return this.__r_store.definition.__r_injectPathNode(
      () => definition.validatePropertyIn(path, value),
      path,
      this
    )
  }
}

export function isTypedNode(value: any): value is TypedNode {
  return value instanceof TypedNode
}

function isForkNeedProxy(target: any) {
  if (isTerminateValue(target)) return false
  return (
    target instanceof TypedNode ||
    isArray(target) ||
    (typeof target === 'object' &&
      target !== null &&
      getPrototypeOf(target) === objectPrototype)
  )
}
